import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Download, Coins, CheckCircle2, AlertCircle, RotateCcw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { fmtNum, plural } from "@/lib/format";

type PointsResult = {
  total: number;
  valid: number;
  loaded: number;
  points: number;
  rows: { row: number; cedula?: string; email: string; name?: string; points: number; motivo?: string }[];
  errors: { row: number; cedula?: string; email: string; points: string; error: string }[];
};

export const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// FV-486: Entrega de puntos por Excel. Primero valida el archivo; la carga solo ocurre al confirmar.
export function PointsUploadPanel({ onLoaded }: { onLoaded?: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [motivo, setMotivo] = useState("");
  const [result, setResult] = useState<PointsResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const reset = () => { setFile(null); setMotivo(""); setResult(null); };

  const send = async (f: File, confirm: boolean) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("motivo", motivo.trim());
      const { data } = await api.post(`/admin/points/upload?confirm=${confirm}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (confirm) { toast.success(`${plural(data.loaded, "cliente recibió", "clientes recibieron")} puntos (${fmtNum(data.points)} pts en total)`); onLoaded?.(); }
    } catch (err: any) { toast.error(apiError(err)); if (confirm) throw err; }
    finally { setBusy(false); }
  };

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setFile(f);
    setResult(null); // no dejar en pantalla (ni confirmar) el resultado de otro archivo
    send(f, false);
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/admin/points/template", { responseType: "blob" });
      saveBlob(data, "plantilla_entrega_puntos.xlsx");
    } catch { toast.error("No se pudo descargar la plantilla"); }
  };

  const exportResult = () => {
    if (!result) return;
    const ok = result.loaded > 0 ? "Cargado" : "Válido";
    const lines = [
      ...result.rows.map((r) => ({ row: r.row, cells: [r.row, r.cedula, r.email, r.name, r.points, r.motivo || motivo.trim(), ok, ""] })),
      ...result.errors.map((e) => ({ row: e.row, cells: [e.row, e.cedula, e.email, "", e.points, "", "Error", e.error] })),
    ].sort((a, b) => a.row - b.row);
    const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = ["fila;cedula;email;nombre;puntos;motivo;estado;detalle", ...lines.map((l) => l.cells.map(esc).join(";"))].join("\n");
    saveBlob(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }), "resultado_entrega_puntos.csv");
  };

  const loaded = !!result && result.loaded > 0;
  // El motivo general solo es obligatorio si alguna fila válida no trae su propio motivo.
  const needsGeneral = !result || result.rows.some((r) => !r.motivo);
  const canConfirm = !!result && result.valid > 0 && (!needsGeneral || !!motivo.trim());

  return (
    <div className="card-obsidian p-6 space-y-5" data-testid="points-upload-panel">
      <ol className="text-sm text-slate-700 list-decimal pl-5 space-y-1">
        <li>Descarga la plantilla y completa una fila por cliente: su <strong>cédula o correo</strong>, los <strong>puntos</strong> y, si quieres, un <strong>motivo</strong> propio. La hoja “Instrucciones” explica cada columna.</li>
        <li>Sube el archivo: lo revisamos y te mostramos el total y qué filas tienen errores. <strong>Todavía no se entrega nada.</strong></li>
        <li>Escribe el motivo general (se usa en las filas sin motivo) y confirma. Cada cliente recibe una notificación y la carga queda en el historial.</li>
      </ol>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label htmlFor="points-file" className="text-slate-700 text-sm">Archivo Excel (.xlsx) o CSV</Label>
            <button type="button" data-testid="download-points-template-button" onClick={downloadTemplate} className="text-sm text-navy hover:underline flex items-center gap-1">
              <Download className="w-3.5 h-3.5" aria-hidden="true" /> Descargar plantilla
            </button>
          </div>
          <Input id="points-file" data-testid="points-file-input" type="file" accept=".xlsx,.csv" onChange={onFile} disabled={busy || loaded} className="bg-obsidian border-slate-200 text-slate-900" />
          {file && <p className="text-xs text-slate-600 mt-1.5 font-mono">{file.name}</p>}
        </div>
        <div>
          <Label htmlFor="points-motivo" className="text-slate-700 text-sm">Motivo general{needsGeneral ? " *" : ""}</Label>
          <Input id="points-motivo" data-testid="points-motivo-input" value={motivo} onChange={(e: any) => setMotivo(e.target.value)} disabled={busy || loaded} placeholder="Ej. Entrega mensual septiembre 2026" className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
          <p className="text-xs text-slate-600 mt-1.5">Se usa en las filas sin motivo propio. Lo verá cada cliente en su historial y en la notificación.</p>
        </div>
      </div>

      {busy && !result && <div className="flex justify-center py-4" role="status"><Loader2 className="w-5 h-5 text-gold-ink animate-spin" aria-hidden="true" /><span className="sr-only">Revisando el archivo…</span></div>}

      {result && (
        <div className="space-y-3" data-testid="points-upload-result">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            <Stat label="Filas" value={fmtNum(result.total)} />
            <Stat label={loaded ? "Cargadas" : "Válidas"} value={fmtNum(loaded ? result.loaded : result.valid)} tone="ok" />
            <Stat label="Con error" value={fmtNum(result.errors.length)} tone={result.errors.length ? "bad" : undefined} />
            <Stat label={loaded ? "Puntos entregados" : "Puntos a entregar"} value={fmtNum(result.points)} tone="ok" />
          </div>
          {loaded && (
            <p className="text-sm text-emerald-800 flex items-center gap-1.5" role="status">
              <CheckCircle2 className="w-4 h-4" aria-hidden="true" /> Se entregaron {fmtNum(result.points)} puntos a {plural(result.loaded, "cliente", "clientes")}.
            </p>
          )}
          <div className="flex justify-end">
            <button type="button" data-testid="export-points-result-button" onClick={exportResult} className="text-sm text-navy hover:underline flex items-center gap-1">
              <Download className="w-3.5 h-3.5" aria-hidden="true" /> Exportar resultado
            </button>
          </div>
          {result.errors.length > 0 && (
            <div>
              <p className="text-sm text-red-700 flex items-center gap-1.5 mb-1.5">
                <AlertCircle className="w-4 h-4" aria-hidden="true" /> {loaded ? "Filas que no se cargaron" : "Estas filas no se cargarán. Corrígelas en el archivo y vuelve a subirlo, o continúa sin ellas."}
              </p>
              <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="sr-only"><tr><th>Fila</th><th>Cliente</th><th>Problema</th></tr></thead>
                  <tbody>
                    {result.errors.map((e) => (
                      <tr key={e.row} className="border-b border-slate-200 last:border-0">
                        <td className="px-3 py-2 font-mono text-slate-600">Fila {e.row}</td>
                        <td className="px-3 py-2 font-mono text-slate-800 break-all">{e.cedula || e.email || "—"}</td>
                        <td className="px-3 py-2 text-red-700">{e.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        {(file || loaded) && (
          <Button variant="outline" onClick={reset} className="border-slate-200 text-slate-700 rounded-full">
            <RotateCcw className="w-4 h-4 mr-1" aria-hidden="true" /> {loaded ? "Hacer otra carga" : "Empezar de nuevo"}
          </Button>
        )}
        {!loaded && (
          <Button data-testid="points-confirm-button" onClick={() => setConfirming(true)} disabled={busy || !canConfirm} className="gold-gradient-bg text-white font-semibold rounded-full">
            <Coins className="w-4 h-4 mr-1" aria-hidden="true" /> Entregar a {plural(result?.valid ?? 0, "cliente", "clientes")}
          </Button>
        )}
      </div>
      {result && !loaded && result.valid > 0 && !canConfirm && <p className="text-sm text-slate-600 text-right">Escribe el motivo general para poder confirmar: hay filas sin motivo propio.</p>}

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        testid="points-upload-confirm"
        title="¿Entregar los puntos?"
        description={<>
          <p>Vas a entregar <strong>{fmtNum(result?.points)} puntos</strong> a <strong>{plural(result?.valid ?? 0, "cliente", "clientes")}</strong>{motivo.trim() ? <> con el motivo “{motivo.trim()}” (salvo en las filas que traen el suyo)</> : " con el motivo de cada fila"}.</p>
          {!!result?.errors.length && <p>Las {fmtNum(result.errors.length)} filas con error no se cargarán.</p>}
          <p>Los puntos no respetan los topes anuales de las reglas. Esta entrega no se puede deshacer desde aquí.</p>
        </>}
        confirmLabel={`Entregar ${fmtNum(result?.points)} pts`}
        onConfirm={() => send(file!, true)}
      />
    </div>
  );
}

const Stat = ({ label, value, tone }: { label: string; value: string; tone?: "ok" | "bad" }) => (
  <div className={`p-3 rounded-lg border ${tone === "ok" ? "bg-emerald-500/10 border-emerald-500/30" : tone === "bad" ? "bg-red-500/10 border-red-500/30" : "bg-obsidian border-slate-200"}`}>
    <p className={`text-xs ${tone === "ok" ? "text-emerald-800" : tone === "bad" ? "text-red-700" : "text-slate-600"}`}>{label}</p>
    <p className={`text-xl font-serif ${tone === "ok" ? "text-emerald-800" : tone === "bad" ? "text-red-700" : ""}`}>{value}</p>
  </div>
);
